/**
 * CompetitionsPage — список соревнований с управлением.
 * Маршрут: /competitions
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title, Text, Stack, Box, Group, Button, Loader, Center,
  Table, Badge, ActionIcon, Menu,
} from '@mantine/core';
import {
  IconPlus, IconTrophy, IconDotsVertical, IconEdit, IconEye,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../theme/designTokens';
import CompetitionFormModal from '../components/Competitions/CompetitionFormModal';
import CompetitionDetailsModal from '../components/Competitions/CompetitionDetailsModal';

const STATUS_LABELS = {
  draft: { label: 'Черновик', color: 'gray' },
  registration_open: { label: 'Регистрация', color: 'green' },
  registration_closed: { label: 'Рег. закрыта', color: 'orange' },
  in_progress: { label: 'Идёт', color: 'blue' },
  completed: { label: 'Завершено', color: 'violet' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow',
  });
};

const CompetitionsPage = () => {
  const [competitions, setCompetitions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [viewComp, setViewComp] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, subjRes, studRes] = await Promise.all([
        fetch('/api/competitions', { credentials: 'include' }),
        fetch('/api/subjects', { credentials: 'include' }),
        fetch('/api/students?page_size=500', { credentials: 'include' }),
      ]);
      if (compRes.ok) setCompetitions(await compRes.json());
      if (subjRes.ok) setSubjects(await subjRes.json());
      if (studRes.ok) {
        const data = await studRes.json();
        setStudents(data.items || data);
      }
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = () => { setEditingComp(null); setFormOpen(true); };
  const handleEdit = (c) => { setEditingComp(c); setFormOpen(true); };
  const handleView = (c) => { setViewComp(c); };
  const handleFormSuccess = () => { setFormOpen(false); setEditingComp(null); loadData(); };

  if (loading) {
    return (
      <Box px="md" py="md">
        <Center py="xl"><Loader size="md" /></Center>
      </Box>
    );
  }

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Соревнования
            </Title>
            <Text c="dimmed" size="sm">
              Управление соревнованиями, запись участников, отслеживание оплат
            </Text>
          </Box>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Создать соревнование
          </Button>
        </Group>

        {competitions.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <IconTrophy size={48} color={tokens.colors.neutral[3]} stroke={1.5} />
              <Text c="dimmed">Соревнований пока нет</Text>
            </Stack>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Название</Table.Th>
                <Table.Th>Дата</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th>Предметы</Table.Th>
                <Table.Th>Участники</Table.Th>
                <Table.Th>ЛК</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {competitions.map((c) => {
                const statusCfg = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
                return (
                  <Table.Tr
                    key={c.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleView(c)}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <IconTrophy size={16} color={tokens.colors.brand.primary} />
                        <Text size="sm" fw={600}>{c.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(c.event_date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="filled" color={statusCfg.color} radius="xl">
                        {statusCfg.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {(c.subjects || []).map((s) => (
                          <Badge key={s.id} size="xs" variant="filled" color="blue" radius="xl">
                            {s.name}
                          </Badge>
                        ))}
                        {(!c.subjects || c.subjects.length === 0) && (
                          <Text size="xs" c="dimmed">—</Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {c.paid_count}/{c.participant_count}
                        <Text span c="dimmed" fw={400}> оплач.</Text>
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {c.is_active ? (
                        <Badge size="xs" color="green" variant="light" radius="xl">Вкл</Badge>
                      ) : (
                        <Badge size="xs" color="gray" variant="light" radius="xl">Выкл</Badge>
                      )}
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEye size={14} />} onClick={() => handleView(c)}>
                            Подробнее
                          </Menu.Item>
                          <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleEdit(c)}>
                            Редактировать
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <CompetitionFormModal
        opened={formOpen}
        onClose={() => { setFormOpen(false); setEditingComp(null); }}
        onSuccess={handleFormSuccess}
        competition={editingComp}
        subjects={subjects}
      />

      <CompetitionDetailsModal
        opened={!!viewComp}
        onClose={() => setViewComp(null)}
        competition={viewComp}
        onEdit={handleEdit}
        students={students}
        subjects={subjects}
      />
    </Box>
  );
};

export default CompetitionsPage;
